import { Container } from "react-bootstrap";
import BreadCrumb from "../../../components/organisms/Breadcrumb/BreadCrumb";
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  ILeadDto,
  IOperatorPerformanceDetails,
} from "../models/OperatorPerformanceDetails";
import OperatorPerformanceDetailsService from "../services/operatorPerformanceDetailsService";
import { useParams } from "react-router-dom";
import Pagination from "../../../components/molecules/Pagination/Pagination";
import FullWidthCard, {
  CardContentItem,
} from "../../../components/organisms/Cards/full-width-card/FullWidthCard";
import LeadStatusDefinitions from "../../../components/organisms/LeadStatusDefinitions/LeadStatusDefinitions";
import Button from "../../../components/atoms/Buttons/Button/Button";
import LoaderHome from "../../../components/atoms/Loader-home/Loader-home";
import useAllLeads from "../../../hooks/useAllLeads";
import useDownloadLeads from "../../../hooks/useDownloadLeads";
import UseAuthConfig from "../../../hooks/useAuthConfig";
import { LEAD_IDENTIFIERS } from "../../leads-management/models/ILead";

const OperatorPerformanceDetails: React.FC = () => {
  const [data, setData] = useState<IOperatorPerformanceDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { id } = useParams<{ id: string }>();
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const { handleDeleteCard } = useAllLeads();
  const selectedCount = selectedLeadIds.length;
  const { protectedResources } = UseAuthConfig();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        if (!id) {
          setError("Operator ID is required");
          setLoading(false);
          return;
        }

        const result =
          await OperatorPerformanceDetailsService.getOperatorPerformanceDetails(
            id,
            pageNumber
          );

        setData(result);
      } catch (err) {
        setError("Failed to load operator performance details");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, pageNumber, data?.totalLeads]);

  const handleDownloadCsv = async () => {
    if (!id) {
      setError("Operator ID is required for downloading CSV");
      return;
    }

    try {
      await OperatorPerformanceDetailsService.downloadOperatorPerformanceCsv(
        id
      );
    } catch (err) {
      setError("Failed to download CSV");
    }
  };

  const handlePageChange = (page: number) => {
    setPageNumber(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleLeadSelection = useCallback((leadId: number) => {
    setSelectedLeadIds((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  }, []);

  const isLeadSelected = useCallback(
    (leadId: number) => selectedLeadIds.includes(leadId),
    [selectedLeadIds]
  );

  const cardContent: CardContentItem[] = useMemo(
    () => [
      { name: "Lead Name", dbName: "firstName" },
      { name: "Lead Type", dbName: "leadType" as any },
      { name: "Mobile Number", dbName: "mobile" },
      { name: "Company", dbName: "company" as any },
      { name: "Job Title", dbName: "jobTitle" as any },
      { name: "Business Type", dbName: "businessType" },
      { name: "Created Date", dbName: "createdDate" },
      { name: "Email", dbName: "email" },
    ],
    []
  );

  const hadleDeleteLead = async (lead: ILeadDto) => {
    handleDeleteCard(lead);
    const result =
      await OperatorPerformanceDetailsService.getOperatorPerformanceDetails(
        id!,
        pageNumber
      );
    setData(result);
  }

  const { handleConfirm, isLoadingDownloads, handleSelectedConfirm } =
    useDownloadLeads(protectedResources);

  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />

      <div className="operator-overview">
        {loading && (
          <>
            <LoaderHome />
            <LoaderHome />
            <LoaderHome />
          </>
        )}
        {error && <p className="operator-overview__error">{error}</p>}

        {!loading && !error && data && (
          <>
            <div className="operator-overview__summary">
              <div className="operator-overview__summary-top">
                <div>
                  <p className="operator-overview__eyebrow">
                    Operator Overview
                  </p>
                  <h1 className="operator-overview__name">
                    {data.operatorName}
                  </h1>
                </div>
              </div>

              <div className="operator-overview__stats">
                <div className="operator-overview__stat-card">
                  <span className="operator-overview__stat-label">
                    Operator ID
                  </span>
                  <strong className="operator-overview__stat-value">
                    {data.operatorId}
                  </strong>
                </div>

                <div className="operator-overview__stat-card">
                  <span className="operator-overview__stat-label">
                    Total Leads
                  </span>
                  <strong className="operator-overview__stat-value operator-overview__stat-value--blue">
                    {data.totalLeads}
                  </strong>
                </div>
              </div>

              <p className="operator-overview__note">
                <span>Status:</span> Operator performance details loaded
                successfully.
              </p>
            </div>
            <div className="operator-personal-details">
              <div className="operator-personal-details__header">
                <h2>Personal Information</h2>
                <span className="operator-personal-details__badge">
                  {data.operatorType}
                </span>
              </div>

              <div className="operator-personal-details__grid">
                <div className="operator-personal-details__item">
                  <span className="label">Email</span>
                  <span className="value">{data.operatorEmail || "-"}</span>
                </div>

                <div className="operator-personal-details__item">
                  <span className="label">Phone</span>
                  <span className="value">{data.operatorPhone || "-"}</span>
                </div>

                <div className="operator-personal-details__item">
                  <span className="label">Company</span>
                  <span className="value">{data.operatorCompany || "-"}</span>
                </div>

                <div className="operator-personal-details__item">
                  <span className="label">Job Title</span>
                  <span className="value">{data.operatorJobTitle || "-"}</span>
                </div>
              </div>
            </div>

            {/* <div className="operator-overview__actions">
              <button className="operator-overview__action-btn" type="button">
                <span>⚙</span>
                Modify Filters
              </button>

              <button
                onClick={handleDownloadCsv}
                className="operator-overview__action-btn"
                type="button"
              >
                <span>⇩</span>
                Export CSV
              </button>
            </div> */}

            <div className="operator-overview__list">

              <div className="download-btn-section">
                {selectedCount == 0 && (
                  <Button
                    cssClass="btn-primary"
                    text="Export CSV"
                    icon="download"
                    onClick={handleDownloadCsv}
                    activeColor="#ffffff"
                  />
                )}
                {selectedCount > 0 && (
                    <Button
                      cssClass="btn-secondary"
                      text="Clear Selection"
                      onClick={() => setSelectedLeadIds([])}
                    />
                )}

                {selectedCount > 0 && (
                  <Button
                    cssClass="btn-primary"
                    text={
                      `Export (${selectedCount})`
                    }
                    icon="download"
                    onClick={() => {
                      if (selectedLeadIds.length > 0) {
                        handleSelectedConfirm(selectedLeadIds);
                      } else {
                        handleConfirm(LEAD_IDENTIFIERS.ALL_LEADS);
                      }
                    }}
                    activeColor="#ffffff"
                  />
                )}
              </div>
              {data.leads.length > 0 ? (
                data.leads.map((lead, key) => (
                  <FullWidthCard
                    key={lead.id}
                    cardContent={cardContent as CardContentItem[]}
                    data={lead as any}
                    deleteCard={() => hadleDeleteLead(lead)}
                    isSelected={isLeadSelected(lead.id)}
                    onSelect={() => toggleLeadSelection(lead.id)}
                    isLeads={true}
                    canEdit={false}
                    isShowAction={true}
                    pathUrl="/leads/all-leads"
                  />
                ))
              ) : (
                <p>No leads found.</p>
              )}

              <Pagination
                pageNumber={data.PageNumber}
                pageCount={data.TotalPages}
                onPageChange={handlePageChange}
                totalPages={data.TotalPages}
                pageSize={data.PageSize}
                totalRecords={data.totalLeads}
                label="leads"
              />
            </div>

            <LeadStatusDefinitions />
          </>
        )}
      </div>
    </Container>
  );
};

export default OperatorPerformanceDetails;
